import { distanceInWords, format, isAfter } from 'date-fns'

import { Blank } from '../../../types'

export const DATE_FORMATS: { [key: string]: { date: string; dateTime: string; time: string; dayMonth: string } } = {
  'cs-CZ': {
    date: 'D. M. YYYY',
    dateTime: 'D. MMMM YYYY H:mm:ss',
    time: 'H:mm',
    dayMonth: 'D.M.',
  },
  'en-US': {
    date: 'M/D/YYYY',
    dateTime: 'dddd, MMMM D, YYYY h:mm:ss a',
    time: 'h:mm a',
    dayMonth: 'M/D',
  },
}

// tslint:disable-next-line:no-empty
export function noop() {}

// tslint:disable-next-line:no-unused-variable
export function identity(_: any, ...options: any[]) {
  return _
}

export function isString(x: any): x is string {
  return typeof x === 'string'
}

export function isNumber(x: any): x is number {
  return typeof x === 'number'
}

export function isBoolean(obj: any): obj is boolean {
  return typeof obj === 'boolean'
}

// tslint:disable-next-line:ban-types
export function isFunction(x: any): x is Function {
  return typeof x === 'function'
}
// tslint:disable-next-line:ban-types
export function isNotFunction(x: any): x is never {
  return typeof x !== 'function'
}

export function isArray(obj: any): obj is any[] {
  return Array.isArray(obj)
}

export function isJsObject(o: any): o is object {
  return o !== null && (typeof o === 'function' || typeof o === 'object')
}

export function isObjectLike<O extends object>(val: any): val is O {
  return isPresent(val) && typeof val === 'object'
}

export function isDate(obj: any): obj is Date {
  return obj instanceof Date && !isNaN(obj.valueOf())
}

export function isPresent<T>(obj: any): obj is T {
  return obj !== undefined && obj !== null
}

export function isQualityMandatory(
  loaded: Array<{ mandatory: boolean; qualityId: number; evaluation: number | null; [key: string]: any }>,
  shaver: string
) {
  if (shaver === 'managerComment') {
    return loaded
      .map(
        (item) =>
          item.mandatory && (item[shaver] && !!item.evaluation ? { ...item } : { emptyQualityID: item.qualityId })
      )
      .filter(Boolean)
  } else {
    return loaded
      .map((item) => item.mandatory && (item[shaver] ? { ...item } : { emptyQualityID: item.qualityId }))
      .filter(Boolean)
  }
}

export function isQualityEmpty(qualities: any) {
  return qualities.map((item: any) => item.emptyQualityID).filter(Boolean)
}

export function isEmpty(obj: any): obj is Blank {
  if (isString(obj) || isArray(obj)) {
    return obj.length === 0
  }
  if (isNumber(obj)) {
    return obj === 0
  }
  if (isJsObject(obj)) {
    return Object.keys(obj).length === 0
  }

  throw new Error('Must be string, number, array or object.')
}

export function isBlank(obj: any): obj is Blank {
  return obj === undefined || obj === null
}

export function has<P extends string>(obj: any, prop: P): obj is { [K in P]: any } {
  return obj && obj.hasOwnProperty(prop)
}

export function isOdd(num: number) {
  // tslint:disable-next-line:no-magic-numbers
  return num % 2 !== 0
}

export function debug(val: any, pretty = true): string {
  // tslint:disable-next-line:no-magic-numbers
  return JSON.stringify(val, null, (pretty && 2) || undefined)
}

export function toString(val?: any): string | undefined {
  if (isPresent(val)) {
    return String(val)
  }
}

export function toNumber(val?: string): number | undefined {
  if (isPresent(val)) {
    return parseFloat(val!)
  }
}

export function sleep(ms: number) {
  return new Promise((resolve, reject) => {
    setTimeout(resolve, ms)
  })
}

/**
 * Exhaustiveness checking utility function
 * - this is used if every use case is catched within switch or if else branching
 * see https://egghead.io/lessons/typescript-use-the-never-type-to-avoid-code-with-dead-ends-using-typescript
 * @param x
 * @param throwError
 * @example
 * ```ts
 * function reducer( state, action ){
 * switch (action.type) {
 *   case GET_DATA_SUCCESS:
 *     return {...state }
 *    case GET_DATA_ERROR:
 *     return {
 *       ...state,
 *       errorLoading: true
 *     }
 *   default: {
 *     // if all actio.type are not covered it will error
 *     assertNever(action)
 *     return state
 *   }
 * }
 * ```
 */
export function assertNever(x: never, throwError = false): never {
  if (throwError) {
    throw new Error(`Unexpected object:  ${x}`)
  }

  return x
}

export function getDisplayDate(date: string | Date) {
  const localeSettings = getLocales()

  return format(date, localeSettings.formats.date, { locale: localeSettings.locale })
}

export function getDisplayDateTime(date: string | Date) {
  const localeSettings = getLocales()

  return format(date, localeSettings.formats.dateTime, { locale: localeSettings.locale })
}

export function getDisplayTime(date: string | Date) {
  const localeSettings = getLocales()

  return format(date, localeSettings.formats.time, { locale: localeSettings.locale })
}

export function getDisplayDateMonth(date: string | Date) {
  const localeSettings = getLocales()

  return format(date, localeSettings.formats.dayMonth, { locale: localeSettings.locale })
}

export function getYear(date: string | Date) {
  return new Date(date).getFullYear()
}

export function getDisplayDateFromNow(date: string | Date) {
  const correctedDate = isAfter(date, new Date()) ? new Date() : date
  const localeSettings = getLocales()

  return distanceInWords(new Date(), correctedDate, { locale: localeSettings.locale, addSuffix: true })
}

export function getCurrentYear() {
  return new Date().getFullYear()
}

export function getLocales() {
  const localeSettings = (window as any).__LocaleSettings

  return isEmpty(localeSettings) ? undefined : localeSettings
}



// WEBPACK FOOTER //
// ./src/app/shared/utils/lang.ts